import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Handshake, CheckCircle, XCircle, Clock } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/common";
import { api, errMsg, money, fmtDate, fmtDateTime, todayStr } from "@/lib/api";

const ST = { pending: ["Yanıt bekleniyor", "text-orange-700", Clock], confirmed: ["Onaylandı", "text-teal-700", CheckCircle], disputed: ["İtiraz edildi", "text-red-700", XCircle] };

export default function ReconciliationDialog({ open, onOpenChange, account }) {
  const [email, setEmail] = useState(account?.email || "");
  const [asOf, setAsOf] = useState(todayStr());
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState([]);
  const load = () => api.get(`/accounts/${account.id}/reconciliations`).then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { if (open) { setEmail(account?.email || ""); load(); } }, [open]); // eslint-disable-line

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/accounts/${account.id}/reconciliation`, { email, as_of: asOf });
      toast.success(`Mutabakat mektubu ${data.email} adresine gönderildi`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl rounded-[6px]" data-testid="reconciliation-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display"><Handshake weight="fill" className="text-[#2dd4bf]" /> Cari Mutabakat</DialogTitle>
          <DialogDescription>Seçilen tarihteki bakiye cariye e-postayla gönderilir; cari tek tıkla onaylar veya itiraz eder, sonuç burada görünür.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="E-posta"><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="reconciliation-email-input" /></Field>
          <Field label="Mutabakat tarihi"><Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} data-testid="reconciliation-date-input" /></Field>
        </div>
        <Button onClick={send} disabled={busy || !email} className="rounded-[4px]" data-testid="reconciliation-send-button">{busy ? "Gönderiliyor…" : "Mutabakat Mektubu Gönder"}</Button>
        <div className="mt-2 max-h-56 overflow-y-auto border text-sm" data-testid="reconciliation-list">
          {list.length === 0 ? <p className="p-3 text-xs text-muted-foreground">Henüz mutabakat gönderilmedi.</p> : list.map((r) => {
            const [label, cls, Icon] = ST[r.status] || ST.pending;
            return (
              <div key={r.id} className="flex items-start justify-between gap-3 border-b p-3 last:border-0" data-testid={`reconciliation-row-${r.id}`}>
                <div>
                  <div className="font-num font-semibold">{money(Math.abs(r.balance))} <span className="text-xs font-sans font-normal text-muted-foreground">({r.balance >= 0 ? "cari borçlu" : "cari alacaklı"}) · {fmtDate(r.as_of)}</span></div>
                  <div className="text-xs text-muted-foreground">{r.email} · gönderim {fmtDateTime(r.created_at)}{r.responded_at ? ` · yanıt ${fmtDateTime(r.responded_at)}` : ""}</div>
                  {r.note && <div className="mt-1 text-xs italic text-red-700">“{r.note}”{r.claimed_balance != null ? ` · beyan: ${money(r.claimed_balance)}` : ""}</div>}
                </div>
                <span className={`inline-flex shrink-0 items-center gap-1 text-xs font-semibold ${cls}`} data-testid={`reconciliation-status-${r.id}`}><Icon weight="fill" /> {label}</span>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
